/**
 * <ia-search-articles>, the blog tab of the search page.
 *
 * Shopify gives articles neither filters nor a usable sort (its three options
 * are relevance and two price sorts, and price pushes non-products to the end),
 * so the category boxes and the ordering are ours. They can only be honest about
 * the WHOLE answer, and Shopify hands back at most 250 results per render (its
 * hard maximum, measured). Past that the answer arrives in pages, so the rest is
 * fetched and spliced in before anything is sorted or counted: a "sorted" or
 * "counted" slice is a lie told confidently.
 *
 * The controls sit in the Filter & Sort drawer, and `ia-drawer` moves itself to
 * `<body>` on connect, out of this element's subtree. So the listener is on the
 * document, and `data-controls` names the drawer this instance owns: a page may
 * hold two blog tabs (two sections, or the editor previewing one beside the
 * other), and a component that grabbed every `[data-article-chip]` in sight
 * would filter its neighbour's grid. The listener is released on disconnect,
 * because a tab switch removes this element and one that outlived its grid
 * would sort a grid that is no longer on the page.
 */
import { captureError } from 'ia-error-report';

const SORTS = {
  relevance: (a, b) => Number(a.dataset.articleIndex) - Number(b.dataset.articleIndex),
  date: (a, b) => Number(b.dataset.articleDate || 0) - Number(a.dataset.articleDate || 0),
  title: (a, b) => (a.dataset.articleTitle || '').localeCompare(b.dataset.articleTitle || ''),
};

class IaSearchArticles extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-ia-article-grid]');
    if (!this.grid) return;

    this.batch = Number(this.grid.dataset.batchSize) || 12;
    this.shown = this.batch;
    this.blogs = new Set();

    // remove-then-add: connectedCallback runs again whenever the node is moved
    // (the engine stashes and restores this area), and a second listener would
    // answer every change twice.
    this.onChange ||= (event) => this.handle(event.target);
    this.onClick ||= (event) => this.act(event.target);
    document.removeEventListener('change', this.onChange);
    document.removeEventListener('click', this.onClick);
    document.addEventListener('change', this.onChange);
    document.addEventListener('click', this.onClick);
    this.querySelector('[data-article-more]')?.addEventListener('click', () => {
      this.shown += this.batch;
      this.reveal();
    });

    const pages = Number(this.grid.dataset.pages) || 1;
    if (pages > 1) {
      this.completeAnswer(pages);
    } else {
      this.applyInitialSort();
    }
  }

  /**
   * Pages 2..N, fetched and spliced in. `data-partial` stands while they travel:
   * the controls are dead in that state, because a grid holding the first page
   * of a longer answer can be neither sorted nor counted truthfully. The blog grid
   * paginates by 50 and reaches it on every visit of a blog past that; the
   * search page's window is 250 and reaches it only past that many posts.
   */
  async completeAnswer(pages) {
    // On the drawer too: the controls live there, outside this element, so that
    // is where the blocked state has to be readable from.
    this.setAttribute('data-partial', '');
    this.controls?.setAttribute('data-partial', '');
    const answers = await Promise.all(
      Array.from({ length: pages - 1 }, (_, index) => this.fetchPage(index + 2))
    );
    const rest = answers.filter(Boolean);
    const lost = answers.length - rest.length;
    // A tab switch during the fetch takes this grid off the page; splicing into
    // it then would spend the work on nobody and repaint a bar it no longer owns.
    if (!this.isConnected) return;

    // Keyed by the index each cell was born with: pages do not overlap, but an
    // article published between two requests shifts the boundary, and the same
    // post twice would be counted twice and sorted next to itself.
    const known = new Set(this.cells.map((cell) => cell.dataset.articleIndex));
    const cells = document.createDocumentFragment();
    rest.forEach((page) => page.cells.forEach((cell) => {
      const index = cell.dataset.articleIndex;
      if (known.has(index)) return;
      known.add(index);
      cells.appendChild(cell);
    }));
    this.grid.appendChild(cells);
    rest.forEach((page) => this.adoptCategories(page));

    // A page that never arrived means the grid is still a slice, and sorting or
    // counting a slice is the exact lie this whole path exists to avoid. The
    // controls stay blocked and the shopper keeps a plain, honest list.
    if (lost > 0) {
      console.error('[ia-search] blog pages missing', lost, 'of', pages - 1);
      // A page the store refused is the report (a throttle as a warning); a
      // page the connection dropped keeps the list partial in silence, the
      // same rule as search:count.
      if (this.pageRefused) {
        captureError(new Error(`blog answer incomplete: ${lost} of ${pages - 1} pages missing`), {
          where: 'search:pages',
          ...(this.pageRefused === 429 ? { level: 'warning' } : {}),
          pages: String(pages),
          status: String(this.pageRefused),
        });
      }
      return;
    }
    this.removeAttribute('data-partial');
    this.controls?.removeAttribute('data-partial');

    // Now, and only now, the controls speak for the whole answer.
    this.recount();
    this.applyInitialSort();
    // Through pick(), not reveal(): recount can untick a category that turned
    // out empty, and the choice is only ever read back off the boxes.
    this.pick();
  }

  /**
   * One page of the same search: its cells, and the category rows and pills it
   * rendered for itself. A category can live only beyond page 1, and rather than
   * building a row for it here, the page that found it is asked for its own.
   */
  async fetchPage(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', this.dataset.section);
    url.searchParams.set('page', page);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        this.pageRefused = response.status;
        return null;
      }
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return {
        cells: [...doc.querySelectorAll('[data-ia-article-grid] [data-article-blog]')],
        rows: [...doc.querySelectorAll('[data-article-row]')],
        pills: [...doc.querySelectorAll('[data-filter-pill]')],
      };
    } catch {
      return null;
    }
  }

  /** Rows and pills this drawer has never seen, taken from the page that has. */
  adoptCategories(page) {
    const list = this.controls?.querySelector('[data-article-rows]');
    const track = this.bar?.querySelector('.ia-filter-bar__pills-track');
    page.rows.forEach((row) => {
      const handle = row.dataset.articleRow;
      if (list && !list.querySelector(`[data-article-row="${CSS.escape(handle)}"]`)) {
        list.appendChild(row);
      }
    });
    page.pills.forEach((pill) => {
      const id = pill.dataset.filterPill;
      if (track && !track.querySelector(`[data-filter-pill="${CSS.escape(id)}"]`)) {
        track.appendChild(pill);
      }
    });
  }

  /**
   * Category counts, and which categories exist at all, read from the grid. The
   * section could only count the page it rendered, so after a splice its numbers
   * are stale and a category living beyond page 1 is still standing hidden.
   */
  recount() {
    const totals = new Map();
    this.cells.forEach((cell) => {
      const blog = cell.dataset.articleBlog;
      totals.set(blog, (totals.get(blog) || 0) + 1);
    });
    this.boxes.forEach((box) => {
      const total = totals.get(box.value) || 0;
      const row = box.closest('[data-article-row]');
      const slot = row?.querySelector('[data-article-count]');
      if (slot) slot.textContent = total;
      if (row) row.hidden = total === 0;

      // A category that turned out empty cannot stay ticked, or the grid would
      // show nothing with no visible reason why.
      if (total === 0 && box.checked) box.checked = false;
    });
  }

  /** The order the drawer opened on, applied once the grid is complete. */
  applyInitialSort() {
    const picked = this.controls?.querySelector('[data-article-sort]:checked');
    if (picked && picked.value !== 'relevance') this.sort(picked.value);
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onChange);
    document.removeEventListener('click', this.onClick);
  }

  /**
   * The drawer this instance owns, wherever it currently sits. Looked up on
   * every use rather than cached: the filter engine re-renders the drawer on
   * each transition, so today's node is not tomorrow's.
   */
  get controls() {
    return document.querySelector(`[data-drawer-id="${this.dataset.controls}"]`);
  }

  /** The filter bar of the same drawer: pills, count and the Clear controls. */
  get bar() {
    return document.querySelector(`[data-filter-bar="${this.dataset.controls}"]`);
  }

  /** The category boxes, wherever the drawer currently sits. */
  get boxes() {
    return [...(this.controls?.querySelectorAll('[data-article-chip]') || [])];
  }

  /** Cells, in the order they currently sit in the DOM. */
  get cells() {
    return [...this.grid.querySelectorAll('[data-article-blog]')];
  }

  /**
   * One handler for both groups: a drawer control either names an order or
   * names a category, and either way the grid is re-read from the controls
   * rather than from remembered state, so the two can never disagree.
   */
  handle(control) {
    const controls = this.controls;
    if (!controls || !controls.contains(control)) return;

    if (control.matches('[data-article-sort]')) {
      this.sort(control.value);
      return;
    }
    if (control.matches('[data-article-chip]')) {
      this.pick();
    }
  }

  /**
   * Clicks the bar owns: a pill drops its own category, Clear drops the lot.
   * Both act on the boxes and then re-read them, so the drawer and the bar can
   * never tell the shopper two different stories.
   */
  act(target) {
    const pill = target.closest?.('[data-filter-pill]');
    const clear = target.closest?.('[data-filter-clear]');
    if (!pill && !clear) return;
    if (!this.bar?.contains(target) && !this.controls?.contains(target)) return;

    if (pill) {
      const box = this.boxes.find((item) => item.value === pill.dataset.filterPill);
      if (box) box.checked = false;
    } else {
      this.boxes.forEach((box) => { box.checked = false; });
    }
    this.pick();
  }

  /** Re-reads the choice from the boxes: they are the state, nothing else is. */
  pick() {
    this.blogs = new Set(this.boxes.filter((box) => box.checked).map((box) => box.value));
    this.shown = this.batch;
    this.reveal();
  }

  sort(value) {
    const compare = SORTS[value];
    if (!compare) return;
    // Moving existing nodes, never rebuilding: the images are already decoded
    // and the shopper is looking at them. Relevance is the server's order, kept
    // as the index every cell was born with.
    const fragment = document.createDocumentFragment();
    this.cells.sort(compare).forEach((cell) => fragment.appendChild(cell));
    this.grid.appendChild(fragment);
    this.shown = this.batch;
    this.reveal();
  }

  /**
   * One pass decides both questions a cell can be hidden by: the categories it
   * does not belong to, and the batch it has not been reached by. Two passes
   * would let a filtered grid reveal fewer cards than the button promises.
   */
  reveal() {
    let visible = 0;
    this.cells.forEach((cell) => {
      const matches = this.blogs.size === 0 || this.blogs.has(cell.dataset.articleBlog);
      const withinBatch = matches && visible < this.shown;
      if (matches) visible += 1;
      cell.style.display = withinBatch ? '' : 'none';
    });
    const more = this.querySelector('[data-article-more]');
    if (more) more.hidden = visible <= this.shown;
    this.paintBar(visible);
  }

  /**
   * The bar says what the products tab's bar says, through the same hooks the
   * server fills there: the pills of what is picked, the number of results
   * behind them, the accent dot, and a way to clear. Pills are revealed rather
   * than built, because the section already rendered one per category.
   */
  paintBar(visible) {
    const active = this.blogs.size > 0;
    [this.bar, this.controls].forEach((root) => {
      if (!root) return;
      root.querySelectorAll('[data-filter-pill]').forEach((pill) => {
        pill.hidden = !this.blogs.has(pill.dataset.filterPill);
      });
      root.querySelectorAll('[data-filter-clear]').forEach((button) => {
        button.hidden = !active;
      });
      root.querySelectorAll('[data-filter-count]').forEach((slot) => {
        slot.textContent = visible;
        slot.classList.toggle('is-active', active);
      });
      root.querySelectorAll('[data-filter-active]').forEach((node) => {
        node.classList.toggle('is-active', active);
      });
      // This tab never re-renders from the server, so both labels ride on the
      // element and the section's choice comes as an attribute.
      root.querySelectorAll('[data-label-active][data-label-empty]').forEach((button) => {
        button.textContent = active ? button.dataset.labelActive : button.dataset.labelEmpty;
        button.classList.toggle('ia-filter-apply', active);
        if (button.hasAttribute('data-hide-when-empty')) {
          const holder = button.closest('ia-drawer-close') || button;
          holder.hidden = !active;
        }
      });
    });
    this.bar?.querySelector('[data-filter-pills]')?.classList.toggle('is-empty', !active);
  }
}

if (!customElements.get('ia-search-articles')) {
  customElements.define('ia-search-articles', IaSearchArticles);
}
